import { cn } from "@/lib/utils";
import { useI18n } from "@/hooks/useI18n";
import {
  MediaItem,
  UNSPLASH_HOME_URL,
  hasUnsplashAttribution,
  withUnsplashUtm,
} from "@/types/media.types";

interface UnsplashAttributionProps {
  item: MediaItem | null | undefined;
  /**
   * Il contenitore deve essere `relative` e stringersi sull'immagine: la banda si
   * ancora al suo bordo inferiore.
   */
  className?: string;
}

/**
 * Attribuzione richiesta dalle Unsplash API Guidelines:
 * "Photo by <fotografo> on Unsplash", con nome linkato al profilo e "Unsplash"
 * linkato alla pagina della foto (fallback: unsplash.com). Tutti i link portano
 * i parametri di referral.
 *
 * Non renderizza nulla per immagini di altri provider (RateHawk, storage, URL
 * esterni) né per le foto Unsplash legacy salvate prima dell'introduzione di
 * MediaItem, per le quali il nome del fotografo non è recuperabile.
 */
export const UnsplashAttribution = ({ item, className = "" }: UnsplashAttributionProps) => {
  const { t } = useI18n();

  if (!hasUnsplashAttribution(item)) return null;

  const { photographerName, photographerUrl, photoUrl } = item.unsplash;

  return (
    <p
      // cn/twMerge: le utility passate dal chiamante vincono sui default in modo
      // deterministico (es. px-2 su px-1.5), senza dipendere dall'ordine nel CSS.
      className={cn(
        "absolute inset-x-0 bottom-0 z-10 truncate bg-black/55 px-1.5 py-0.5 text-[10px] leading-tight text-white",
        className
      )}
      // Evita che il click sull'attribuzione apra il lightbox/la card sottostante.
      onClick={(e) => e.stopPropagation()}
    >
      {t("modals.unsplashAttributionPrefix")}{" "}
      <a
        href={withUnsplashUtm(photographerUrl)}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="underline hover:text-white"
      >
        {photographerName}
      </a>{" "}
      {t("modals.unsplashAttributionOn")}{" "}
      <a
        href={withUnsplashUtm(photoUrl || UNSPLASH_HOME_URL)}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="underline hover:text-white"
      >
        Unsplash
      </a>
    </p>
  );
};

export default UnsplashAttribution;
